import { buildConfigPointer, countClass, currentUser, pointer, pointerId, queryAllClass, queryClass, saveClassRecord } from './parse';
import type { ParseRecord } from './parse';
import type { VideoProgressEntry } from './videoProgress';
import { loadMyCustomer } from './account';

// Notes live only on this device; scope them per user like local progress.
function localNotesKey(): string {
  return `ayurguide-v3-notes:${pointerId(currentUser()) ?? 'anon'}`;
}
export type PersonalNote = { id: string; title: string; body: string; updatedAt: string };

function realUserId(): string | undefined {
  const id = pointerId(currentUser());
  return id && id !== 'local-student' ? id : undefined;
}

/* ---------- Bookmarks (watched lectures synced from the player) ---------- */

export type BookmarkRow = ParseRecord & { objectId: string; Bookmarks?: VideoProgressEntry[]; CustomerId?: unknown; UserId?: unknown };

export async function loadWatchedLectures(): Promise<BookmarkRow> | never {
  const userId = realUserId();
  if (!userId) return { objectId: '', Bookmarks: [] } as BookmarkRow;
  const customer = await loadMyCustomer().catch(() => undefined);
  const identities = [
    ...(customer?.objectId ? [pointer('elearning_Customer', customer.objectId)] : []),
    pointer('_User', userId),
  ];
  // Prefer the row owned by this session. Legacy mobile rows may be readable
  // through CustomerId but cannot be updated by the V3 student API.
  for (const identity of identities) {
    const rows = await queryClass('elearning_Bookmarks', {
      limit: 1,
      appScoped: false,
      keys: ['Bookmarks', 'CustomerId', 'UserId'],
      where: (query) => {
        query.equalTo('AppId', buildConfigPointer());
        query.equalTo('CustomerId', identity);
        query.equalTo('UserId', pointer('_User', userId));
      },
    }) as BookmarkRow[];
    if (rows[0]) return rows[0];
  }
  for (const identity of identities) {
    const rows = await queryClass('elearning_Bookmarks', {
      limit: 1,
      appScoped: false,
      keys: ['Bookmarks', 'CustomerId', 'UserId'],
      where: (query) => {
        query.equalTo('AppId', buildConfigPointer());
        query.equalTo('CustomerId', identity);
      },
    }) as BookmarkRow[];
    if (rows[0]) return rows[0];
  }
  return { objectId: '', Bookmarks: [], CustomerId: identities[0] } as BookmarkRow;
}

export async function loadVideoTitles(ids: string[]): Promise<Map<string, ParseRecord>> {
  if (!ids.length) return new Map();
  const rows = await queryClass('elearning_Videos', {
    limit: ids.length,
    keys: ['VideoName', 'VideoDuration'],
    appScoped: false,
    where: (query) => query.containedIn('objectId', ids),
  });
  return new Map(rows.map((row) => [row.objectId, row]));
}

export async function saveBookmarkRow(row: BookmarkRow, entries: VideoProgressEntry[]): Promise<BookmarkRow> {
  const userId = realUserId();
  if (!userId) throw new Error('Sign in to save lecture bookmarks.');
  let customerId = row.CustomerId;
  if (!customerId) {
    const customer = await loadMyCustomer().catch(() => undefined);
    customerId = customer?.objectId ? pointer('elearning_Customer', customer.objectId) : pointer('_User', userId);
  }
  const ownedByCurrentUser = pointerId(row.UserId) === userId;
  const saved = await saveClassRecord(
    'elearning_Bookmarks',
    { Bookmarks: entries, CustomerId: customerId },
    ownedByCurrentUser ? row.objectId || undefined : undefined,
  );
  return { ...row, ...saved, CustomerId: customerId, UserId: pointer('_User', userId), Bookmarks: entries } as BookmarkRow;
}

/* ---------- Notes (stored on this device) ---------- */

export function loadLocalNotes(): PersonalNote[] {
  localStorage.removeItem('ayurguide-v3-notes'); // purge pre-user-scoping shared data
  try { return JSON.parse(localStorage.getItem(localNotesKey()) || '[]'); } catch { return []; }
}

export function saveLocalNotes(notes: PersonalNote[]) {
  localStorage.setItem(localNotesKey(), JSON.stringify(notes));
}

/* ---------- Notifications ---------- */

export async function loadNotifications(page = 0, pageSize = 15): Promise<{ items: ParseRecord[]; total: number }> {
  const where = (query: { equalTo: (k: string, v: unknown) => void }) => query.equalTo('AppId', buildConfigPointer());
  const [items, total] = await Promise.all([
    queryClass('elearning_Notifications', { limit: pageSize, skip: page * pageSize, order: '-createdAt', where }) as Promise<ParseRecord[]>,
    countClass('elearning_Notifications'),
  ]);
  return { items, total };
}

/* ---------- Results & performance ---------- */

export type ResultRow = ParseRecord & {
  TestTitle?: string;
  CourseTitle?: string;
  ObtainedMarks?: number;
  TotalMarks?: number;
  Percentage?: number;
  YourGrade?: string;
  TestDate?: string;
  TestInfoPtr?: { objectId?: string } | string;
};

export async function loadAllMyResults(max = Infinity): Promise<ResultRow[]> {
  const userId = realUserId();
  if (!userId) return [];
  const customer = await loadMyCustomer().catch(() => undefined);
  const identities = [pointer('_User', userId), ...(customer?.objectId ? [pointer('elearning_Customer', customer.objectId)] : [])];
  const pages = await Promise.all(identities.map((identity) => queryAllClass('test_TestResult', {
    limit: Math.max(max, 100),
    order: '-TestDate',
    appScoped: false,
    where: (query) => query.equalTo('CustomerPtr', identity),
  }) as Promise<ResultRow[]>));
  const rows = [...new Map(pages.flat().map((row) => [row.objectId, row])).values()]
    .sort((left, right) => String(right.TestDate ?? right.createdAt ?? '').localeCompare(String(left.TestDate ?? left.createdAt ?? '')));
  return max === Infinity ? rows : rows.slice(0, max);
}
